<template>
    <div class="profile">
        <ConfirmAccountPassword ref="confirmAccountPassword" />
        <v-row>
            <v-col col="12" class="d-flex justify-space-between align-center">
                <h1>{{ $t('user.user_details') }}</h1>
            </v-col>
            <v-col class="filter-box d-flex justify-end" cols="12" md="6">
                <div class="btn-box">
                    <v-btn v-if="!editing" @click.stop="editing = true" color="#08a657" height="56px" depressed dark
                           class="btn-two mobile-block">
                        <v-icon>mdi-pencil-outline</v-icon>
                        <span class="d-none d-md-inline">{{ $t('user.edit') }}</span>
                    </v-btn>
                    <v-btn v-else :loading="savingUser" :disabled="savingUser" @click.stop="saveUser" color="#08a657" height="56px" depressed dark
                           class="btn-two mobile-block profile__btn-save"
                           :class="{ fixed: scrollClassBtnSave }"
                           v-scroll="handleScroll"
                    >
                        <v-icon>mdi-content-save-outline</v-icon>
                        <span class="d-none d-md-inline">{{ $t('user.save') }}</span>
                    </v-btn>
                    <div v-if="editing" class="profile__btn-save--shadow" :class="{ fixed: scrollClassBtnSave }"></div>
                </div>
            </v-col>
        </v-row>
        <v-row justify="center">
            <v-col col="12" s="10" md="8" lg="6" xl="5">
                <v-card>
                    <v-card-text>
                        <v-row>
                            <v-col col="12" class="text-right">
                                <v-text-field
                                        v-model="user.userName"
                                        :rules="[required]"
                                        :label="$t('user.username')"
                                        required
                                        outlined
                                        :clearable="editing"
                                        :disabled="!editing"
                                        ref="userName"
                                ></v-text-field>
                                <v-dialog v-model="dialogChangePassword" width="500">
                                    <template v-slot:activator="{ on }">
                                        <v-btn color="primary" text v-on="on">
                                            {{ $t('user.change_password') }}
                                        </v-btn>
                                    </template>

                                    <v-card>
                                        <v-card-title>
                                            {{ $t('user.change_password') }}
                                        </v-card-title>
                                        <v-card-text>
                                            <v-alert v-if="passwordError" type="error" outlined>
                                                {{ passwordError }}
                                            </v-alert>
                                            <v-text-field
                                                    v-model="password"
                                                    :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                                                    :rules="[required, minPassLength]"
                                                    :type="show1 ? 'text' : 'password'"
                                                    :label="$t('user.password')"
                                                    counter
                                                    outlined
                                                    :clearable="editing"
                                                    @click:append="show1 = !show1"
                                                    ref="changePasswordForm1"
                                            ></v-text-field>
                                            <v-text-field
                                                    v-model="passwordNew"
                                                    :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
                                                    :rules="[required, passwordPolicyRule]"
                                                    :type="show2 ? 'text' : 'password'"
                                                    :label="$t('user.password_new')"
                                                    counter
                                                    outlined
                                                    :clearable="editing"
                                                    @click:append="show2 = !show2"
                                                    ref="changePasswordForm2"
                                                    :maxlength="user.role.toLocaleLowerCase() === 'signing user' ? '4' : undefined"
                                            ></v-text-field>
                                            <PasswordRequirements
                                                    v-if="administrativeRole"
                                                    :value="passwordNew"
                                            />
                                            <v-text-field
                                                    v-model="passwordConfirm"
                                                    :append-icon="show3 ? 'mdi-eye' : 'mdi-eye-off'"
                                                    :rules="[required, confirm]"
                                                    :type="show3 ? 'text' : 'password'"
                                                    :label="$t('user.password_confirm')"
                                                    counter
                                                    outlined
                                                    :clearable="editing"
                                                    @click:append="show3 = !show3"
                                                    ref="changePasswordForm3"
                                                    :maxlength="user.role.toLocaleLowerCase() === 'signing user' ? '4' : undefined"                                                  
                                            ></v-text-field>
                                        </v-card-text>

                                        <v-divider></v-divider>

                                        <v-card-actions>
                                            <v-spacer></v-spacer>
                                            <v-btn right text color="primary" @click="dialogChangePassword = false">
                                                {{ $t('cancel') }}
                                            </v-btn>
                                            <v-btn right text color="primary" :disabled="!canSubmitPassword" @click="passwordChange">
                                                {{ $t('apply') }}
                                            </v-btn>
                                        </v-card-actions>
                                    </v-card>
                                </v-dialog>
                            </v-col>
                        </v-row>

                        <v-divider></v-divider>
                        <v-subheader class="mt-7">{{ $t('user.personal_data')}}</v-subheader>

                        <v-row>
                            <v-col col="12">
                                <v-text-field
                                        v-model="user.name"
                                        :label="$t('user.name')"
                                        outlined
                                        :clearable="editing"
                                        :disabled="!editing"
                                        ref="name"
                                ></v-text-field>
                                <v-text-field
                                        v-model="user.surname"
                                        :label="$t('user.surname')"
                                        outlined
                                        :clearable="editing"
                                        :disabled="!editing"
                                        ref="surname"
                                ></v-text-field>

                                {{$t('user.signature')}}
                                <img v-if="signatureImg" :src="signatureImg"/>

                                <v-btn color="primary" text
                                       @click="dialogChangeSignature.length === 0 ? dialogChangeSignature = [0] : dialogChangeSignature = []">
                                    <v-icon v-if="!signatureImg" class="ma-0">mdi-plus</v-icon>
                                    <v-icon v-else class="ma-0">mdi-pencil-outline</v-icon>
                                </v-btn>

                                <v-expansion-panels v-model="dialogChangeSignature" multiple>
                                    <v-expansion-panel>
                                        <v-expansion-panel-content class="pt-4">
                                            <Signature
                                                    width="200"
                                                    height="100"
                                                    lineWidth="1"
                                                    ref="signature"
                                                    :img="signatureImg"
                                                    :signatureChange="signatureChange"
                                            />
                                            <v-btn right text color="primary" @click="signatureClear"
                                                   class="float-right">
                                                {{ $t('cancel') }}
                                            </v-btn>
                                            <v-btn right text color="primary" @click="signatureGet" class="float-right">
                                                {{ $t('apply') }}
                                            </v-btn>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-col>
                        </v-row>

                        <v-divider></v-divider>
                        <v-subheader class="mt-7">{{ $t('user.organization_data')}}</v-subheader>

                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                        v-if="user.role === 'Driver User'"
                                        v-model="user.transicsId"
                                        :label="$t('user.transics_id')"
                                        outlined
                                        :clearable="editing"
                                        :disabled="!editing"
                                ></v-text-field>
                                <v-autocomplete
                                        :items="countryList"
                                        :label="$t('organization.country_code')"
                                        item-text="name"
                                        item-value="code"
                                        v-model="user.operationCountryCode"
                                        outlined
                                        :disabled="true"
                                        :no-data-text="$t('noDataAvailable')"
                                >
                                    <template v-slot:selection="data">
                                        {{ data.item.name }} - {{ data.item.code }}
                                    </template>
                                    <template v-slot:item="data">
                                        {{ data.item.name }} - {{ data.item.code }}
                                    </template>
                                </v-autocomplete>
                                <v-text-field
                                        v-model="user.operationPostalCode"
                                        :label="$t('user.operation_postal_code')"
                                        outlined
                                        :disabled="true"
                                ></v-text-field>
                                <v-text-field
                                        v-model="user.operationPlace"
                                        :label="$t('user.operation_place')"
                                        outlined
                                        :disabled="true"
                                ></v-text-field>
                                <v-text-field
                                        v-model="user.operationStreet"
                                        :label="$t('user.operation_street')"
                                        outlined
                                        :disabled="true"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import Axios from 'axios'

    import config from '../config'
    import {logout as LogoutFunc} from '@/services/logout.js'

    import Signature from '@/components/Signature'
    import ConfirmAccountPassword from '@/components/ConfirmAccountPassword'
    import {hasSensitiveAccountChanges} from '@/utils/sensitiveAccountChange'
    import PasswordRequirements from '@/components/PasswordRequirements'
    import {isAdministrativeRole, isStrongPassword} from '@/utils/passwordPolicy'

    const qs = require('querystring')

    export default {
        components: {
            Signature,
            ConfirmAccountPassword,
            PasswordRequirements
        },
        data() {
            return {
                user: {
                    id: '',
                    rolesId: '',
                    organizationsId: '',
                    surname: '',
                    name: '',
                    phone: '',
                    address: '',
                    birthDate: '',
                    userName: '',
                    password: '',
                    guid: '',
                    jobTitleName: '',
                    email: '',
                    role: '',
                    employeeStatus: '',
                    beginDate: '',
                    endDate: '',
                    interfaceCountryCode: '',
                    transicsId: '',
                    defaultSignatureSign: '',
                    signature: '',
                    operationCountryCode: '',
                    operationPostalCode: '',
                    operationPlace: '',
                    operationStreet: '',
                },
                userReset: {},
                editing: false,
                savingUser: false,
                dialogChangePassword: false,
                password: '',
                passwordConfirm: '',
                passwordNew: '',
                show1: false,
                show2: false,
                show3: false,
                menuBirthday: false,
                menuBeginDate: false,
                menuEndDate: false,
                dialogChangeSignature: [],
                scrollClassBtnSave: false,
                countryList: [],
                passwordError: '',
            }
        },
        computed: {
            signatureImg() {
                if (this.user.signature === '') return false;
                return `data:image/png;base64,${this.user.signature}`;
            },
            administrativeRole() {
                return isAdministrativeRole(this.user.role || localStorage.getItem('role'));
            },
            passwordIsValid() {
                return this.administrativeRole
                    ? isStrongPassword(this.passwordNew)
                    : this.passwordNew.length >= 4;
            },
            canSubmitPassword() {
                return !!this.password &&
                    this.passwordIsValid &&
                    this.passwordConfirm === this.passwordNew;
            },
        },
        created() {
            this.getEmployeeList();
            this.getCountriesList();
        },
        methods: {
            resetUser() {
                this.user = Object.assign({}, this.userReset);
            },
            applyUser() {
            },
            required(value) {
                return !!value || this.$i18n.t('user.required')
            },
            minPassLength(v){
                return v.length >= 4 || this.$i18n.t('password_length_characters', {passLength: 4})
            },
            passwordPolicyRule(value) {
                if (this.administrativeRole) {
                    return isStrongPassword(value) || 'The password does not meet the required complexity.';
                }
                return this.minPassLength(value);
            },
            min(v) {
                return v === '' || v.length >= 6 || this.$i18n.t('user.min_6')
            },
            emailValidation(v) {
                /* eslint-disable */
                const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return v === null || v === '' || re.test(v) || this.$i18n.t('user.email_correct_error_msg');
            },
            confirm(v) {
                return v === this.passwordNew || this.$i18n.t('user.password_confirm_error')
            },
            resetFormPassword() {
                this.password = '';
                this.passwordNew = '';
                this.passwordConfirm = '';
                this.passwordError = '';
            },
            passwordChange() {
                this.passwordError = '';
                if (this.$refs.changePasswordForm1.validate() && this.$refs.changePasswordForm2.validate() && this.$refs.changePasswordForm3.validate()) {
                    let $this = this;
                    let payload = Object.assign({}, this.userReset);
                    payload.OldPassword = this.password;
                    payload.NewPassword = this.passwordNew;
                    Axios.post(config.apiBaseUrl + 'set-password', qs.stringify(payload), {
                        headers: {
                            'Authorization': 'Bearer ' + localStorage.getItem('accessToken'),
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    })
                        .then(function (response) {
                            if (response.status === 200) {
                                $this.dialogChangePassword = false;
                                LogoutFunc();
                            }
                        })
                        .catch(function (error) {
                        if (error.sessionExpiryHandled) return;
                            const responseData = error.response && error.response.data;
                            $this.passwordError =
                                (responseData && responseData.message) ||
                                responseData ||
                                'Unable to change the password.';
                        })
                }
            },
            closeDialogChangePassword() {
                this.dialogChangePassword = false;
                this.resetFormPassword();
            },
            signatureGet() {
                this.$refs.signature.sendSignature();
                this.dialogChangeSignature = [];
            },
            signatureChange(data) {
                data = data.split('data:image/png;base64,')[1];
                this.user.signature = data;
                this.signatureChangeSend(data)
            },
            signatureClear() {
                this.dialogChangeSignature = [];
                this.$refs.signature.clear();
            },
            signatureChangeSend(signature) {
                let $this = this;
                let payload = Object.assign({}, this.userReset);
                payload.signature = signature;
                Axios.post(config.apiBaseUrl + 'api/employee/edit', qs.stringify(payload), {
                    headers: {
                        'Authorization': 'Bearer ' + localStorage.getItem('accessToken'),
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                })
                    .catch(function (error) {
                        if (error.sessionExpiryHandled) return;
                        /* eslint-disable */
                        alert(error.response.data)
                    })
            },
            async saveUser() {
                if (this.savingUser || !this.$refs.userName.validate()) return;
                this.savingUser = true;
                const savedUser = {...this.user};
                const payload = {...savedUser, operationCountryCode: '', operationPostalCode: '', operationPlace: '', operationStreet: ''};
                const save = UserPassword => Axios.post(config.apiBaseUrl + 'api/employee/edit',
                    qs.stringify({...payload, UserPassword}), {
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('accessToken'),
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    });
                try {
                    const response = hasSensitiveAccountChanges(payload, this.userReset)
                        ? await this.$refs.confirmAccountPassword.request(save)
                        : await save();
                    if (response && response.status === 200) {
                        const oldName = this.userReset.userName;
                        this.userReset = savedUser;
                        this.editing = false;
                        if (oldName !== savedUser.userName) LogoutFunc();
                    }
                } catch (error) {
        if (error.sessionExpiryHandled) return;
                    alert((error.response && error.response.data) || 'Unable to save changes.');
                } finally { this.savingUser = false; }
            },
            handleScroll: function () {
                this.scrollClassBtnSave = window.scrollY > 92;
            },
            getCountriesList() {
                let $this = this;
                Axios.get(config.apiBaseUrl + 'api/organization/country-list', {
                        headers: {
                            'Authorization': 'Bearer ' + localStorage.getItem('accessToken'),
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                )
                    .then(function (response) {
                        if (response.status === 200) {
                            $this.countryList = response.data;
                        }
                    })
                    .catch(function (error) {
                        if (error.sessionExpiryHandled) return;
                        setError(error)
                    })
            },
            getEmployeeList() {
                const payload = {
                    Id: localStorage.getItem('userId'),
                };
                let $this = this;
                Axios.post(config.apiBaseUrl + 'api/employee/list', qs.stringify(payload), {
                        headers: {
                            'Authorization': 'Bearer ' + localStorage.getItem('accessToken'),
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                )
                    .then(function ({status, data}) {
                        if (status === 200 && data.data.length === 1) {
                            $this.user = data.data[0];
                            $this.userReset = Object.assign({}, $this.user);
                        }
                    })
                    .catch(function (error) {
                        if (error.sessionExpiryHandled) return;
                        /* eslint-disable */
                        console.log(error)
                    })

            },
        },
        watch: {
            dialogChangePassword(val) {
                val || this.closeDialogChangePassword()
            },
            dialogChangeSignature(val) {
                this.$emit('openDialog');
            }
        }
    }
</script>
